import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Search, Brain, ListChecks, Globe, Mail, GraduationCap,
  ArrowDown, CheckCircle2, XCircle, Clock, Eye, Shield, Workflow
} from 'lucide-react';

const steps = [
  {
    icon: Brain,
    title: 'Phase 0 — Knowledge Base Check',
    description: 'The agent first checks the internal Knowledge Base for known delisting methods for the target blacklist. If high-confidence methods exist, they are prioritized.',
    detail: 'Each method has a success rate and confidence score based on previous attempts across all agents.',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
  },
  {
    icon: Search,
    title: 'Phase 1 — Web Research',
    description: 'If no reliable knowledge exists, the agent performs a web search (via Tavily API) to find delisting procedures for the blacklist.',
    detail: 'The agent also uses Playwright to scrape the blacklist website directly and extract form fields, contact info, and instructions.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
  },
  {
    icon: ListChecks,
    title: 'Phase 2 — Checklist Creation',
    description: 'An LLM analyzes all gathered information and creates a prioritized checklist of delisting methods, ordered by confidence score.',
    detail: 'Methods include: web form submission, email request, API calls, auto-expire (waiting), and manual steps. The checklist is merged with existing KB methods.',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
  },
  {
    icon: Globe,
    title: 'Phase 3a — Web Form Automation',
    description: 'For web_form methods, the agent uses Playwright to navigate to the delisting page, fill in the IP address and required fields, and submit the form.',
    detail: 'The browser extracts the DOM tree and sends it as text to the LLM for decision-making. If a CAPTCHA is detected, CapSolver attempts to solve it. No vision model needed — fully DOM-based interaction.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
  },
  {
    icon: Mail,
    title: 'Phase 3b — Email Request',
    description: 'For email methods, the agent composes a professional delisting request using an LLM and sends it via the Postal API.',
    detail: 'The email is sent from delist-{job-id}@agent.provider.tools. When a reply arrives via webhook, an LLM analyzes the response to determine if the delisting was successful.',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
  },
  {
    icon: GraduationCap,
    title: 'Phase 4 — Learning',
    description: 'After each attempt (success or failure), the agent updates the Knowledge Base with the result.',
    detail: 'Success rates are updated, new methods are recorded, tips and notes are stored. This knowledge is shared across all agent instances for future jobs.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
  },
];

const dataPrivacy = [
  {
    label: 'DeepSeek V4 Pro (Standard-Modus)',
    description: 'Primary LLM for email composition, page analysis, checklist creation, search synthesis. Schnell, JSON-native, kosteneffizient.',
    models: 'deepseek-v4-pro (non-thinking)',
    badge: 'Primary',
    color: 'text-violet-400',
  },
  {
    label: 'DeepSeek V4 Pro (Thinking-Modus)',
    description: 'Strategisches Reasoning: Fehleranalyse, Methoden-Priorisierung, adaptive Planung. Deep Chain-of-Thought für komplexe Entscheidungen.',
    models: 'deepseek-v4-pro (thinking mode)',
    badge: 'Max Effort',
    color: 'text-cyan-400',
  },
];

export default function ProcessPage() {
  return (
    <div className="space-y-8" data-testid="process-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Agent Workflow</h2>
        <p className="text-muted-foreground">How the autonomous delisting agent processes each job</p>
      </div>

      {/* Process Steps */}
      <div className="relative">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-4 mb-2">
            {/* Vertical line + icon */}
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-lg ${step.bg} flex items-center justify-center shrink-0`}>
                <step.icon className={`w-5 h-5 ${step.color}`} />
              </div>
              {i < steps.length - 1 && (
                <div className="w-px h-full bg-border min-h-[24px]" />
              )}
            </div>

            {/* Content */}
            <Card className="flex-1 mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{step.title}</CardTitle>
                <CardDescription>{step.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{step.detail}</p>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Outcomes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            Possible Outcomes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Success</p>
                <p className="text-xs text-muted-foreground">IP removed from blacklist. Method success rate increased in KB.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10">
              <Clock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Waiting</p>
                <p className="text-xs text-muted-foreground">Email sent, awaiting response. Auto-timeout after 48h.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-500/10">
              <XCircle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Failed</p>
                <p className="text-xs text-muted-foreground">All methods exhausted. Failure data stored for future learning.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Privacy */}
      <Card>
        <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                AI Architecture — DeepSeek V4 Pro
              </CardTitle>
              <CardDescription>
                Ein Modell, zwei Modi: Non-Thinking für schnelle Tasks, Thinking-Mode für strategisches Reasoning
              </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dataPrivacy.map((zone) => (
              <div key={zone.label} className="p-4 rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`font-semibold text-sm ${zone.color}`}>{zone.label}</span>
                  <Badge variant="outline" className="text-xs">{zone.badge}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-1">{zone.description}</p>
                <p className="text-xs font-mono text-muted-foreground">Models: {zone.models}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Email Flow */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Flow
          </CardTitle>
          <CardDescription>How the agent sends and receives delisting emails</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Badge className="bg-primary/20 text-primary border-0 shrink-0">1</Badge>
            <span>Agent composes email via DeepSeek (V3)</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Badge className="bg-primary/20 text-primary border-0 shrink-0">2</Badge>
            <span>Email sent from <code className="text-xs bg-secondary px-1 py-0.5 rounded">delist-{'{'}<em>job-id</em>{'}'}@agent.provider.tools</code> via Postal API</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Badge className="bg-primary/20 text-primary border-0 shrink-0">3</Badge>
            <span>Replies routed by Postal to <code className="text-xs bg-secondary px-1 py-0.5 rounded">POST /api/webhook/email?token=...</code></span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Badge className="bg-primary/20 text-primary border-0 shrink-0">4</Badge>
            <span>LLM analyzes response → determines success/failure → updates job status</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
